import {
  IAlternativeSsoFormValues,
  IDealerTrackAlternativeSsoFormValues,
  IRouteOneAlternativeSsoFormValues,
} from 'modules/auth/auth';
import { EMPTY_STRING, LOGIN_URL_PREFIX } from 'modules/core/constants';
import { DealSourceProvider } from 'modules/core/enums';
import { IDealSourceData } from 'modules/core/types/dealSource';

const ETG_LENDER_ID_MAX_SIZE = 6;
const REQUIRED_ROUTE_ONE_ALTERNATIVE_SSO_PARAMS = ['conversation_id', 'dealer_id'] as const;
const REQUIRED_DEALERTRACK_ALTERNATIVE_SSO_PARAMS = [
  'conversation_id',
  'dealer_id',
  'finance_company_id',
  'deal_source',
] as const;

export interface IAlternativeSsoLocation {
  hostname?: string;
  search?: string;
}

const hasAllParams = (searchParams: URLSearchParams, params: readonly string[]): boolean =>
  params.every((param) => searchParams.has(param) && searchParams.get(param));

const resolveLocation = (location?: IAlternativeSsoLocation): { hostname: string; search: string } => ({
  hostname: location?.hostname ?? window.location.hostname,
  search: location?.search ?? window.location.search,
});

const isOnLoginHost = (hostname: string): boolean => hostname.includes(LOGIN_URL_PREFIX);

const isDealerTrackDealSource = (dealSource: string | null): boolean => {
  const normalized = dealSource?.trim().toLowerCase();

  return normalized === 'dt' || normalized === DealSourceProvider.DEALER_TRACK;
};

const isDealerTrackAlternativeSso = (searchParams: URLSearchParams): boolean =>
  hasAllParams(searchParams, REQUIRED_DEALERTRACK_ALTERNATIVE_SSO_PARAMS) &&
  isDealerTrackDealSource(searchParams.get('deal_source'));

export const isAlternativeSsoLink = (location?: IAlternativeSsoLocation): boolean => {
  const { hostname, search } = resolveLocation(location);
  const searchParams = new URLSearchParams(search);

  return (
    isOnLoginHost(hostname) &&
    !!search &&
    REQUIRED_ROUTE_ONE_ALTERNATIVE_SSO_PARAMS.every((param) => searchParams.has(param))
  );
};

export const getAlternativeSsoFormValues = (location?: IAlternativeSsoLocation): IAlternativeSsoFormValues => {
  const { search } = resolveLocation(location);
  const searchParams = new URLSearchParams(search);

  if (isDealerTrackAlternativeSso(searchParams)) {
    return REQUIRED_DEALERTRACK_ALTERNATIVE_SSO_PARAMS.reduce((values, param) => {
      values[param] = searchParams.get(param)!;

      return values;
    }, {} as IDealerTrackAlternativeSsoFormValues);
  }

  const values = REQUIRED_ROUTE_ONE_ALTERNATIVE_SSO_PARAMS.reduce((values, param) => {
    values[param] = searchParams.has(param) ? searchParams.get(param)! : EMPTY_STRING;

    return values;
  }, {} as IRouteOneAlternativeSsoFormValues);

  const financeCompanyId = searchParams.get('finance_company_id');

  if (financeCompanyId) {
    values.finance_company_id = financeCompanyId;
  }

  return values;
};

const isLenderPrefixMatch = (queryLenderId: string, dealSourceLender?: string): boolean =>
  queryLenderId.substring(0, ETG_LENDER_ID_MAX_SIZE) === dealSourceLender?.substring(0, ETG_LENDER_ID_MAX_SIZE);

const isConversationIdUpdated = (searchParams: URLSearchParams, dealSourceData: IDealSourceData): boolean => {
  const queryConversationId = searchParams.get('conversation_id');

  if (isDealerTrackAlternativeSso(searchParams)) {
    return (
      queryConversationId === dealSourceData.dt_api_conversation_id ||
      queryConversationId === dealSourceData.conversation_id
    );
  }

  return queryConversationId === dealSourceData.conversation_id;
};

export const isDealSourceDataUpdated = (
  dealSourceData: IDealSourceData,
  location?: IAlternativeSsoLocation,
): boolean => {
  const { search } = resolveLocation(location);
  const searchParams = new URLSearchParams(search);
  const financeCompanyId = searchParams.get('finance_company_id');

  if (
    !isConversationIdUpdated(searchParams, dealSourceData) ||
    searchParams.get('dealer_id') !== dealSourceData.dealer_id
  ) {
    return false;
  }

  if (!financeCompanyId) {
    return true;
  }

  return isLenderPrefixMatch(financeCompanyId, dealSourceData.lender);
};
